import { OrderPlanningRowAction, OrderPlanningRowActionProps, ReasonMap } from "./OrderPlanningRowAction";
import { Button, ClickEvent, Label, Panel, TableRow, Toast } from "@mcleod/components";
import { ActionPermission, HorizontalAlignment, ModelRow } from "@mcleod/core";
import { getDispatchControlBoolean, getDispatchControlValue } from "@mcleod/general";
import { Preassignment, PreassignmentSlideoutProps } from "../../asset/Preassignment";
import { CommonDialogs } from "@mcleod/common";
import { OrderPlanningSplitView } from "../opsmgmt/OrderPlanningSplitView";
import { RowDriverOnlyFields } from "../../../asset/src/models/autogen/AutogenModelDriverOnly";
import { RowDispatchControlFields } from "@mcleod/general/src/models/autogen/AutogenModelDispatchControl";

interface ExecuteProps extends OrderPlanningRowActionProps {
    splitViewLayout: OrderPlanningSplitView;
    sourceEvent?: ClickEvent;
}

export class RowActionOrdersManageAssignments extends OrderPlanningRowAction {
    override getButton(): Button {
        return this.toolPanel.buttonManageAssign;
    }
    override getButtonId(): string {
        return "buttonManageAssign";
    }

    override getAdditionalProps(event: ClickEvent): Partial<ExecuteProps> {
        return {
            splitViewLayout: this.toolPanel.splitViewLayout as OrderPlanningSplitView,
            sourceEvent: event,
            ...this.getFindNearAfterSlideoutProp()
        }
    }

    override getFieldsUsedInThisFile(): string[] {
        return ["dispatchby_preassign_count", "brokerage", "hold", "manifest_hold", "revenue_code"];
    }

    override getMaxSelections(): keyof RowDispatchControlFields {
        return "max_a_assign_selections";
    }

    override getMultiSelectPermission(): ActionPermission {
        return ActionPermission.ACTION_MULTISELECT_PREASSIGN;
    }

    override actionValidateSelection(selectedRows: ModelRow[], reasonMap: ReasonMap): void {
        if (!this.passesLinkedCheck(selectedRows, reasonMap)) {
            return;
        }
        selectedRows.forEach(row => this.rowValidator(reasonMap, row));
    }

    private rowValidator(reasonMap: ReasonMap, modelRow: ModelRow): void {
        const arrayField = "order_id";
        if (modelRow.getBoolean("brokerage", false)) {
            this.addReason(reasonMap, "brokered", "Orders [{0}] are brokerage and cannot be preassigned.", modelRow, arrayField);
        }

        if (getDispatchControlBoolean("prevent_dsp_hold") && modelRow.getBoolean("hold", false)) {
            this.addReason(reasonMap, "on_hold", "Orders [{0}] cannot be preassigned while on hold.", modelRow, arrayField);
        }

        if (modelRow.getBoolean("manifest_hold", false)) {
            this.addReason(reasonMap, "manifest_on_hold", "Manifested orders [{0}] cannot be preassigned while on hold.", modelRow, "manifest_id");
        }

        if (getDispatchControlBoolean("enforce_revenu_cod") && modelRow.isNull("revenue_code")) {
            this.addReason(reasonMap, "no_revenue_code", "Revenue codes are enforced and required for orders [{0}] for preassignment.", modelRow, arrayField);
        }
    }

    private passesLinkedCheck(selectedRows: ModelRow[], reasonMap: ReasonMap): boolean {
        const linkedMap = {};
        let hasUnlinked = false;
        selectedRows.forEach(row => {
            if (row.isNull("linked_id")) {
                hasUnlinked = true;
            } else {
                linkedMap[row.get("linked_id")] = (linkedMap[row.get("linked_id")] || 0) + 1;
            }
        });
        if (Object.entries(linkedMap).length > 0 && hasUnlinked) {
            this.addReason(reasonMap, "linked_mixed", "Cannot select a combination of linked and unlinked groups for preassignment.");
            return false;
        } else if (Object.entries(linkedMap).length > 1) {
            this.addReason(reasonMap, "linked_multiple", "Cannot select multiple linked groups for preassignment.");
            return false;
        }
        return true;
    }

    override async execute(executeProps: ExecuteProps): Promise<void> {
        const modelRow = executeProps.selectedRows[0]?.data as ModelRow;
        if (modelRow == null)
            return;
        const selectedRows = executeProps.selectedRows;
        const isMultiSelect = selectedRows.length > 1 || !selectedRows[0].data.isNull("linked_id");

        const isPreassigned = () => {
            return !modelRow.isNull("driver1_id") && !modelRow.isNull("tractor");
        }

        const moveId = modelRow.get("movement_id");
        const orderId = modelRow.get("order_id");
        const manifestId = modelRow.get("manifest_id");
        const equipGroupId = modelRow.get("equip_group_id");

        const setEquipmentProps = (props: PreassignmentSlideoutProps, modelRow: ModelRow) => {
            props.tractor = modelRow.get("tractor", null);
            const driver1Data = this.extractDriverData(modelRow, "driver1");
            const driver2Data = this.extractDriverData(modelRow, "driver2");

            if (driver1Data != null) {
                props.driver1Data = driver1Data;
            }

            if (driver2Data != null) {
                props.driver2Data = driver2Data;
            }
        }

        const props: PreassignmentSlideoutProps = {
            movementId: moveId,
            orderId: orderId,
            manifestId: manifestId,
            preassigned: !isMultiSelect && isPreassigned(),
            equipmentGroupId: equipGroupId,
            doAfterSlideout: async (name: string, showToast: boolean, multipleSelected: boolean) => {
                if (showToast) {
                    if (multipleSelected) {
                        Toast.showSuccessToast("Multiple orders were successfully assigned.");
                    } else {
                        Toast.showToast(Preassignment.getResultsToastPanel(orderId, name, manifestId));
                    }
                }
            }
        };

        const equipRow = executeProps.splitViewLayout.getActiveEquipLayout()["getTable"]()?.selectedRow?.data;
        const hasSelectedEquipment = (equipRow != null && (!equipRow.isNull("tractor") || !equipRow.isNull("driver1_id")));
        if (isMultiSelect) {
            if (selectedRows.some(row => !row.data.isNull("linked_id"))) {
                props.linkedId = selectedRows[0].data.get("linked_id");
            }
            props.selectedRows = selectedRows;
            if (hasSelectedEquipment) {
                setEquipmentProps(props, equipRow);
            }
            const assignedCount = selectedRows.filter(row => (!row.data.isNull("tractor") || !row.data.isNull("driver1_id"))).length;
            if (assignedCount > 0) {
                const multiOverwritePanel = this.createMultiOverwritePanel(selectedRows);
                const overwriteAssignments = await CommonDialogs.showYesNo(multiOverwritePanel, "Update Preassignments", { xVisible: false});
                if (overwriteAssignments == undefined || !overwriteAssignments) {
                    return;
                }
            }
        } else {
            let overwriteAssignment = false;
            const tractorOrDriverAssigned = !modelRow.isNull("tractor") || !modelRow.isNull("driver1_id"); // not the same as isPreassigned()
            if (tractorOrDriverAssigned && hasSelectedEquipment) {
                const overwritePrompt: Panel = this.createOverwritePanel(modelRow);
                overwriteAssignment = await CommonDialogs.showYesNo(overwritePrompt, "Update Preassignment",
                    { yesButtonCaption: "Yes, Update Assignment", noButtonCaption: "No, View Current Assignment(s)" });
                if (overwriteAssignment == undefined)
                    return;
            }

            const useSecondaryModelRow = !isPreassigned()
                && equipRow != null
                && (!equipRow.isNull("tractor") || !equipRow.isNull("driver1_id"))
                && modelRow.isNull("tractor")
                && modelRow.isNull("driver1_id");

            if (tractorOrDriverAssigned && !overwriteAssignment) {
                setEquipmentProps(props, modelRow);
            } else if (overwriteAssignment || useSecondaryModelRow) {
                setEquipmentProps(props, equipRow);
                if (overwriteAssignment) {
                    const dispatchBy = getDispatchControlValue("dispatch_by");
                    const shouldUnassign = dispatchBy == "T"
                        ? !modelRow.isNull("tractor") && modelRow.get("tractor") != equipRow.get("tractor")
                        : !modelRow.isNull("driver1_id") && modelRow.get("driver1_id") != equipRow.get("driver1_id");
                    props.preassigned = false;
                    props.equipmentGroupId = null;
                    props.unassigning = shouldUnassign;
                }
            }
            if (!modelRow.isNull("trailer"))
                props.trailer = modelRow.get("trailer", null);
            if (!modelRow.isNull("trailer2"))
                props.trailer2 = modelRow.get("trailer2", null);
            if (!modelRow.isNull("trailer3"))
                props.trailer3 = modelRow.get("trailer3", null);
        }
        props.sourceEvent = executeProps.sourceEvent;
        Preassignment.showSlideout(props, {doAfterSlideOut: executeProps.afterSlideout});
    }

    private extractDriverData(modelRow: ModelRow, fieldPrefix: string): Partial<RowDriverOnlyFields> {
        if (modelRow.isNull(fieldPrefix + "_id")) {
            return null;
        }
        return {
            id: modelRow.get(fieldPrefix + "_id"),
            first_name: modelRow.get(fieldPrefix + "_name_first"),
            name: modelRow.get(fieldPrefix + "_name_last"),
            full_name: modelRow.get(fieldPrefix + "_name"),
        }
    }

    private createOverwritePanel(modelRow: ModelRow): Panel {
        const message = new Label({
            text: this.getOverwriteMessage(modelRow),
            align: HorizontalAlignment.CENTER
        });
        const question = new Label({
            fontBold: true, fontSize: "large",
            text: "Would you like to update the assignment?"
        });
        return new Panel({
            width: 550,
            align: HorizontalAlignment.CENTER,
            components: [message, question]
        });
    }

    private getOverwriteMessage(modelRow: ModelRow): string {
        const driverName = modelRow.get("driver1_name");
        const tractor = modelRow.get("tractor");
        if (driverName && tractor) {
            return `This movement is currently assigned to ${driverName} in Tractor ${tractor}?`;
        } else if (!driverName && tractor) {
            return `This movement is currently assigned to Tractor ${tractor}?`;
        } else if (driverName && !tractor) {
            return `This movement is currently assigned to ${driverName}`;
        }
    }

    private createMultiOverwritePanel(selectedRows: TableRow[]) {
        const assignedCount = selectedRows.filter(row => (!row.data.isNull("tractor") || !row.data.isNull("driver1_id"))).length;
        const totalCount = selectedRows.length;
        const message = new Label({
            text: `${assignedCount} of the ${totalCount} selected movements are currently assigned.`,
            align: HorizontalAlignment.CENTER
        });
        const question = new Label({
            fontBold: true, fontSize: "large",
            text: `Would you like to update the assignment for all ${totalCount} selected movements?`
        });
        return new Panel({
            width: 550,
            align: HorizontalAlignment.CENTER,
            components: [message, question]
        });
    }
}
